import React, { useState, useEffect } from 'react';

// Hardcoded fallback used when the server /api/models endpoint is unreachable
const FALLBACK_MODELS = [
    { id: 'gemini-3.6-flash', name: 'Gemini 3.6 Flash (Recommended)', description: 'Newest flash — frontier multimodal + agentic', specs: 'Input: 1M tokens, Output: 65K tokens' },
    { id: 'gemini-3.5-flash', name: 'Gemini 3.5 Flash', description: 'Fast frontier multimodal', specs: 'Input: 1M tokens, Output: 65K tokens' },
    { id: 'gemini-3.5-flash-lite', name: 'Gemini 3.5 Flash-Lite', description: 'Fastest/cheapest 3.5', specs: 'Input: 1M tokens, Output: 65K tokens' },
    { id: 'gemini-3.1-pro-preview', name: 'Gemini 3.1 Pro', description: 'Most advanced — reasoning, coding, agentic', specs: 'Input: 1M tokens, Output: 65K tokens' },
    { id: 'gemini-2.5-pro', name: 'Gemini 2.5 Pro', description: 'Stable deep reasoning + coding', specs: 'Input: 1M tokens, Output: 65K tokens' },
    { id: 'gemini-2.5-flash', name: 'Gemini 2.5 Flash', description: 'Stable price-performance', specs: 'Input: 1M tokens, Output: 65K tokens' },
];

// Backward-compatible export for anything importing { models }
export let models = [...FALLBACK_MODELS];

const ModelSelector = ({ selectedModel, setSelectedModel }) => {
    const [modelStatus, setModelStatus] = useState({ loading: false, error: null, success: null });
    const [availableModels, setAvailableModels] = useState(FALLBACK_MODELS);
    const [modelsSource, setModelsSource] = useState(null);

    // Fetch live model list from server on mount
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await fetch('/api/models');
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const data = await res.json();
                if (cancelled || !data.models?.length) return;

                setAvailableModels(data.models);
                models = data.models;
                setModelsSource(data.source);

                // If the currently selected model is not in the fresh list, auto-select first
                const ids = data.models.map(m => m.id);
                if (selectedModel && !ids.includes(selectedModel)) {
                    setSelectedModel(data.models[0].id);
                }
            } catch (err) {
                console.warn('[ModelSelector] Using fallback models:', err.message);
                setModelsSource('fallback');
            }
        })();
        return () => { cancelled = true; };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const testModel = async (model) => {
            if (!model) return;
            setModelStatus({ loading: true, error: null, success: null });
            try {
                // Use relative URL to leverage the proxy configuration in package.json
                const response = await fetch('/api/test-model', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ selectedModel: model }),
                });
                
                // Check if response is valid JSON
                const contentType = response.headers.get('content-type');
                if (!contentType || !contentType.includes('application/json')) {
                    const text = await response.text();
                    console.error('Non-JSON response:', text);
                    throw new Error(`Server returned non-JSON response: ${text.substring(0, 100)}`);
                }
                
                const data = await response.json();
                
                if (!response.ok) {
                    throw new Error(data.error || `HTTP ${response.status}: ${response.statusText}`);
                }
                
                setModelStatus({ loading: false, error: null, success: data.message });
            } catch (err) {
                console.error('Model test error:', err);
                let errorMessage = err.message;
                
                // Handle specific error cases
                if (err.message.includes('Failed to fetch')) {
                    errorMessage = 'Cannot connect to backend server - ensure it\'s running on port 3001';
                } else if (err.message.includes('Proxy error')) {
                    errorMessage = 'Proxy error - backend server may be offline or unreachable';
                } else if (err.message.includes('non-JSON response')) {
                    errorMessage = 'Server error - check backend server logs for details';
                } else if (err.message.includes('Unexpected token')) {
                    errorMessage = 'Invalid response format - server may be returning an error page';
                }
                
                setModelStatus({ loading: false, error: `Model test failed: ${errorMessage}`, success: null });
            }
        };

        if (selectedModel) {
            testModel(selectedModel);
        }
    }, [selectedModel]);

    const styles = {
        container: {
            marginBottom: '1rem',
            width: '100%',
            maxWidth: '500px',
        },
        label: {
            display: 'block',
            color: '#e0e0e0',
            marginBottom: '0.5rem',
            textAlign: 'left',
            fontWeight: 'bold',
        },
        select: {
            width: '100%',
            padding: '12px',
            fontSize: '1rem',
            backgroundColor: '#282c34',
            color: '#e0e0e0',
            border: '1px solid #444',
            borderRadius: '5px',
        },
        descriptionContainer: {
            color: '#a0a0a0',
            fontSize: '0.9rem',
            textAlign: 'left',
            marginTop: '0.5rem',
            minHeight: '60px', // Reserve space for description
        },
        description: {
            margin: '0 0 0.25rem 0',
        },
        specs: {
            margin: 0,
            fontStyle: 'italic',
        },
        statusBox: {
            marginTop: '1.5rem',
            padding: '1rem',
            borderRadius: '4px',
            textAlign: 'center',
        },
        loading: {
            backgroundColor: '#3b3b3b',
            color: '#ccc',
        },
        error: {
            backgroundColor: '#5e3a3a',
            color: '#ffbaba',
        },
        success: {
            backgroundColor: '#3a5e3a',
            color: '#baffba',
        }
    };

    const selectedModelData = availableModels.find(m => m.id === selectedModel);

    return (
        <div style={styles.container}>
            <label style={styles.label} htmlFor="model-select">
                Select AI Model
                {modelsSource === 'api' && <span style={{ fontWeight: 'normal', fontSize: '0.75rem', color: '#4caf50', marginLeft: 8 }}>● Live</span>}
                {modelsSource === 'fallback' && <span style={{ fontWeight: 'normal', fontSize: '0.75rem', color: '#ff9800', marginLeft: 8 }}>● Cached</span>}
            </label>
            <select
                id="model-select"
                value={selectedModel}
                onChange={(e) => setSelectedModel(e.target.value)}
                style={styles.select}
            >
                {availableModels.map(model => (
                    <option key={model.id} value={model.id}>
                        {model.name}
                    </option>
                ))}
            </select>
            <div style={styles.descriptionContainer}>
                {selectedModelData ? (
                    <>
                        <p style={styles.description}>{selectedModelData.description}</p>
                        <p style={styles.specs}>{selectedModelData.specs}</p>
                    </>
                ) : (
                    <p>Select a model to see its description.</p>
                )}
            </div>

            {modelStatus.loading && <div style={{...styles.statusBox, ...styles.loading}}>Testing model...</div>}
            {modelStatus.error && <div style={{...styles.statusBox, ...styles.error}}>{modelStatus.error}</div>}
            {modelStatus.success && <div style={{...styles.statusBox, ...styles.success}}>{modelStatus.success}</div>}
        </div>
    );
};

export default ModelSelector;
